import { useMutation } from "@apollo/client";
import { Download, PlayArrow } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  Input,
} from "@mui/material";
import React, { ReactElement } from "react";

import {
  ComposeLayoutMutation,
  Layout,
} from "../../../../generated/gql/graphql";
import { downloadFile } from "../../../../utils/download-files";

import { COMPOSE_LAYOUT } from "./use-compose-layout";

export interface ComposeLayoutDialogProps {
  layout: Pick<Layout, "id">;

  onClose: () => void;
}

export function ComposeLayoutDialog(
  props: ComposeLayoutDialogProps,
): ReactElement {
  const { layout, onClose } = props;

  const [prompt, setPrompt] = React.useState<string>(
    "Place the objects from image 1 realistically into image 2. Keep the composition, position and size of image 1.",
  );
  const [composeLayout, { data, loading, error, reset }] = useMutation(
    COMPOSE_LAYOUT,
    { errorPolicy: "all" },
  );

  function downloadComposedImage(result: ComposeLayoutMutation): void {
    downloadFile(
      result.composeLayout.composedImage.data,
      result.composeLayout.composedImage.mimeType,
      `composed-layout-${layout.id}`,
    );
  }

  function downloadComposedImageAI(result: ComposeLayoutMutation): void {
    downloadFile(
      result.composeLayout.composedImageAI.data,
      result.composeLayout.composedImageAI.mimeType,
      `composed-layout-${layout.id}-ai`,
    );
  }

  function handleStart(): void {
    composeLayout({
      variables: {
        input: {
          layoutId: layout.id,
          prompt,
        },
      },
      onCompleted: (result) => {
        console.log("Composition result:", result);
      },
    });
  }

  let content;
  if (loading) {
    content = (
      <div className="h-full flex flex-col justify-center items-center">
        <div>
          <CircularProgress />
        </div>
        <div>Composing layout... This may take a moment.</div>
      </div>
    );
  } else if (error) {
    content = (
      <div className="h-full flex flex-col justify-center items-center">
        <p>Error during composition: {error.message}</p>
        <Button onClick={() => reset()}>Try Again</Button>
      </div>
    );
  } else if (data) {
    content = (
      <div>
        <p>
          Composition completed! You can download the composed images below.
        </p>
        <div className="mt-3 flex">
          <Button
            color="primary"
            variant="outlined"
            href={data.composeLayout.presentationRenderingUrl}
            download={`presentation-rendering-${layout.id}`}
          >
            <Download />
            Products Rendering
          </Button>
          <Button
            className="ms-2"
            color="primary"
            variant="outlined"
            href={data.composeLayout.backgroundRenderingUrl}
            download={`background-rendering-${layout.id}`}
          >
            <Download />
            Background Rendering
          </Button>
        </div>
        <div className="mt-3 flex justify-between">
          <div style={{ width: "48%" }}>
            <p>Final Composed Layout:</p>
            <img
              src={`data:${data.composeLayout.composedImage.mimeType};base64,${data.composeLayout.composedImage.data}`}
              alt="Composed Layout"
              style={{ maxWidth: "100%" }}
            />
            <IconButton onClick={() => downloadComposedImage(data)}>
              <Download />
            </IconButton>
          </div>
          <div style={{ width: "48%" }}>
            <p>AI Composed Layout:</p>
            <img
              src={`data:${data.composeLayout.composedImageAI.mimeType};base64,${data.composeLayout.composedImageAI.data}`}
              alt="Composed Layout AI"
              style={{ maxWidth: "100%" }}
            />
            <IconButton onClick={() => downloadComposedImageAI(data)}>
              <Download />
            </IconButton>
          </div>
        </div>
      </div>
    );
  } else {
    content = (
      <div className="w-full flex flex-col">
        <p>Ready to compose your layout with AI-powered composition!</p>
        <FormControl fullWidth className="mt-4">
          <FormLabel>Prompt:</FormLabel>
          <Input
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            fullWidth
            multiline
            minRows={4}
            placeholder="Enter a custom prompt to guide the composition process"
          />
        </FormControl>
      </div>
    );
  }

  return (
    <Dialog open={true} fullWidth={true} maxWidth="md">
      <DialogTitle>AI-Powered Layout Composition</DialogTitle>
      <DialogContent style={{ width: "100%", height: "600px" }}>
        {content}
      </DialogContent>
      <DialogActions className="flex justify-between">
        <Button onClick={onClose} color="error" variant="outlined">
          Close
        </Button>
        {!data ? (
          <Button
            color="primary"
            variant="outlined"
            onClick={handleStart}
            disabled={loading}
          >
            <PlayArrow width={16} height={16} />
            Start
          </Button>
        ) : (
          <Button
            color="primary"
            variant="outlined"
            onClick={reset}
            disabled={loading}
          >
            Try Again
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
